import * as clipboard from 'clipboard-polyfill/text';
import {
  type FC,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import UntitledIcon from '@payaca/untitled-icons';

interface Props {
  children: ReactNode;
}

function extractLanguage(children: ReactNode): string | null {
  // react-markdown renders <pre><code className="language-xxx">…</code></pre>
  const child = Array.isArray(children) ? children[0] : children;
  if (
    child &&
    typeof child === 'object' &&
    'props' in (child as ReactElement)
  ) {
    const props = (child as ReactElement).props as Record<string, unknown>;
    const className = props.className as string | undefined;
    const match = className?.match(/language-(\S+)/);
    return match ? match[1] : null;
  }
  return null;
}

const CodeBlock: FC<Props> = ({ children }) => {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);
  const language = extractLanguage(children);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    const text = preRef.current?.textContent ?? '';
    await clipboard.writeText(text);
    setCopied(true);
  }, []);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-700">
      <div className="flex items-center justify-between bg-gray-800 px-3 py-1.5">
        <span className="text-xs text-gray-400">{language ?? 'code'}</span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex cursor-pointer items-center gap-1 rounded bg-transparent px-1.5 py-0.5 text-xs text-gray-400 transition-colors hover:text-gray-200"
        >
          <UntitledIcon
            className="h-3.5 w-3.5"
            name={copied ? 'check' : 'copy-07.3'}
          />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        ref={preRef}
        className="overflow-x-auto bg-gray-900 p-4 text-sm text-gray-100 [&_code]:border-0 [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit"
      >
        {children}
      </pre>
    </div>
  );
};

export default CodeBlock;
